import notNullish from '@tv2/lib-core/notNullish';
import { sliceCombineDateRanges } from '../Nielsen/sliceCombineRanges';
import type {
  AdobeProgramTrackingFragment,
  AdobeTrackingFragment,
} from '../dataProviders/__generated__/Tracking';

export class TimeTable<T> {
  private fallback: TimeTableFallback<T>;
  private entries: TimeTableEntry<T>[] = [];
  private isStartOver: boolean;

  constructor(isStartOver: boolean, fallback: TimeTableFallback<T>) {
    this.fallback = {
      ...fallback,
      isFallback: true,
    };
    this.isStartOver = isStartOver;
  }

  addEntries(...entries: TimeTableEntry<T>[]) {
    this.entries = sliceCombineDateRanges(this.entries, entries);
  }

  getCurrentEntry(currentTime: Date): TimeTableInformationEntry<T> {
    if (this.isStartOver) {
      return this.entries[0] ?? this.fallback;
    }

    const entries = this.entries.filter(x => {
      return currentTime >= x.start && currentTime <= x.stop;
    });

    if (entries.length > 0) {
      return entries[entries.length - 1];
    }

    // We will try the last entry that is before the current time.
    const lastEntry = this.entries.filter(x => x.start <= currentTime).pop();
    if (lastEntry) {
      return lastEntry;
    }

    return this.fallback;
  }

  getTotalAdBreakDuration(entry: TimeTableInformationEntry<T>) {
    const parent = entry.parent ?? entry.data;

    const children = this.entries.filter(x => x.parent === parent);

    return (
      Math.max(...children.map(x => x.stop.getTime())) -
      Math.min(...children.map(x => x.start.getTime()))
    );
  }

  getAdIndex(entry: TimeTableInformationEntry<T>) {
    const parent = entry.parent ?? entry.data;

    const children = this.entries.filter(x => x.parent === parent);

    return children.indexOf(entry as TimeTableEntry<T>) + 1;
  }
}

export interface TimeTableInformationEntry<T> {
  data: T;
  parent?: T;
  isFallback?: boolean;
}

export interface TimeTableEntry<T> extends TimeTableInformationEntry<T> {
  start: Date;
  stop: Date;
}

export interface TimeTableFallback<T> extends TimeTableInformationEntry<T> {}

function getDateFromAdobeTimestamp(timestamp: number | null | undefined) {
  return new Date(Math.floor(timestamp ?? 0));
}

export class AdobeTimeTableMapper {
  static map(data: AdobeTrackingFragment): TimeTableEntry<AdobeProgramTrackingFragment>[] {
    const result = data.adobe?.nodes?.filter(notNullish).flatMap(node => {
      const { program } = node;
      if (!program) {
        return [];
      }

      const prerolls = node.prerolls?.nodes ?? [];
      const mappedPrerolls = prerolls.filter(notNullish).map(preroll => {
        return {
          start: getDateFromAdobeTimestamp(preroll.start),
          stop: getDateFromAdobeTimestamp(preroll.stop),
          data: preroll,
          parent: program,
        } as TimeTableEntry<AdobeProgramTrackingFragment>;
      });

      return [
        ...mappedPrerolls,
        {
          start: getDateFromAdobeTimestamp(program.start),
          stop: getDateFromAdobeTimestamp(program.stop),
          data: program,
        } as TimeTableEntry<AdobeProgramTrackingFragment>,
      ];
    });

    return result ?? [];
  }
}
